import { type ImgHTMLAttributes, useState } from 'react'
import { cn } from '../lib/utils'

export interface MealPhotoProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError'> {
  /** The meal's photo. */
  src?: string
  /** Shown when `src` fails to load — the meal's placeholder art. */
  fallbackSrc?: string
}

// Diagonal-stripe tile with the concentric-rings glyph — the last resort when no URL loads.
// The stripe is an 11px/1px repeating gradient too specific for a spacing token.
function PlaceholderTile({ alt, className }: { alt: string; className?: string }) {
  return (
    <div
      {...(alt ? { role: 'img', 'aria-label': alt } : { 'aria-hidden': true })}
      className={cn(
        'relative flex items-center justify-center overflow-hidden bg-brand-green-100 text-brand-green-600 dark:bg-brand-green-900 dark:text-brand-green-400',
        className
      )}>
      <div className="absolute inset-0 bg-[repeating-linear-gradient(135deg,transparent_0_11px,rgba(61,90,56,0.05)_11px_12px)] dark:bg-[repeating-linear-gradient(135deg,transparent_0_11px,rgba(255,255,255,0.04)_11px_12px)]" />
      <svg viewBox="0 0 34 34" fill="none" aria-hidden="true" className="relative aspect-square h-auto w-2/5 max-w-[34px] opacity-45">
        <circle cx="17" cy="17" r="14" stroke="currentColor" strokeWidth="2" />
        <circle cx="17" cy="17" r="6" stroke="currentColor" strokeWidth="2" />
      </svg>
    </div>
  )
}

/**
 * A meal photo that never shows the browser's broken-image glyph. If `src` fails to
 * load it swaps to `fallbackSrc`, and if that fails too it draws the striped
 * placeholder tile. `className` sizes whichever one renders, so nothing shifts.
 */
export function MealPhoto({ src, fallbackSrc, alt = '', className, ...imgProps }: MealPhotoProps) {
  // Keyed by URL rather than a flag, so a new `src` gets a fresh attempt.
  const [failed, setFailed] = useState<readonly string[]>([])
  const current = [src, fallbackSrc].find((url): url is string => !!url && !failed.includes(url))

  if (!current) return <PlaceholderTile alt={alt} className={className} />

  return (
    <img
      {...imgProps}
      src={current}
      alt={alt}
      className={className}
      onError={() => {
        console.warn(`[MealPhoto] failed to load ${current}`)
        setFailed((prev) => [...prev, current])
      }}
    />
  )
}
